/**
 * Admin Resource Transforms
 * Field mappings and data transformation functions for Content Manager API
 */

import { API_CONFIG } from '../config/api.config';
import { applyCollectionRules } from './admin-resource-collection-rules';

/** Field mapping for Strapi - frontend field names to Strapi (status -> entity_status) */
export const FIELD_MAPPINGS: Record<string, Record<string, string>> = {
  'api::client.client': { status: 'client_status' },
  'api::provider.provider': { status: 'provider_status' },
  'api::branch.branch': { status: 'branch_status' },
  'api::employee.employee': { status: 'employee_status' },
  'api::employee-document.employee-document': { status: 'employee_document_status' },
  'api::car.car': { status: 'car_status' },
  'api::car-document.car-document': { status: 'car_document_status' },
  'api::invoice.invoice': { status: 'invoice_status' },
  'api::debit-note.debit-note': { status: 'debit_note_status' },
  'api::deduction.deduction': { status: 'deduction_status' },
  'api::purchase.purchase': { status: 'purchase_status' },
  'api::bill.bill': { status: 'bill_status' },
  'api::petty-cash.petty-cash': { status: 'petty_cash_status' },
  'api::payment-order.payment-order': { status: 'payment_order_status' },
  'api::contract.contract': { status: 'contract_status' },
  'api::foa.foa': { status: 'foa_status' },
  'api::invoice-hire.invoice-hire': { status: 'invoice_hire_status' },
  'api::crew-certificate.crew-certificate': { status: 'crew_certificate_status' },
  'api::crew-change.crew-change': { status: 'crew_change_status' },
  'api::crew-list.crew-list': { status: 'crew_list_status' },
  'api::crew-evaluation.crew-evaluation': { status: 'crew_evaluation_status' },
  'api::candidate.candidate': { status: 'candidate_status' },
  'api::personnel-permit.personnel-permit': { status: 'personnel_permit_status' },
  'api::car-permit.car-permit': { status: 'car_permit_status' },
  'api::requisition.requisition': { status: 'requisition_status' },
  'api::requisition-item.requisition-item': { status: 'requisition_item_status' },
  'api::delivery-note.delivery-note': { status: 'delivery_note_status' },
  'api::letter-generation.letter-generation': { status: 'letter_generation_status' },
};

/**
 * Transform field names based on collection type (frontend -> Strapi)
 */
export const transformFieldNames = (data: any, collectionType: string) => {
  const transformed = { ...data };
  const mappings = FIELD_MAPPINGS[collectionType];

  if (mappings) {
    Object.keys(mappings).forEach((oldField) => {
      if (transformed[oldField] !== undefined) {
        transformed[mappings[oldField]] = transformed[oldField];
        delete transformed[oldField];
      }
    });
  }

  return transformed;
};

/**
 * Reverse transform field names (Strapi -> frontend)
 */
export const reverseTransformFieldNames = (data: any, collectionType?: string) => {
  if (!collectionType) return data;

  const transformed = { ...data };
  const mappings = FIELD_MAPPINGS[collectionType];

  if (mappings) {
    // Create reverse mappings (strapiField -> frontendField)
    const reverseMappings: Record<string, string> = {};
    Object.keys(mappings).forEach((frontendField) => {
      reverseMappings[mappings[frontendField]] = frontendField;
    });

    // Apply reverse mappings
    Object.keys(reverseMappings).forEach((strapiField) => {
      if (transformed[strapiField] !== undefined) {
        transformed[reverseMappings[strapiField]] = transformed[strapiField];
        delete transformed[strapiField];
      }
    });
  }

  return transformed;
};

const toNestedCurrency = (obj: any, prefix: string) => {
  const usd = obj[`${prefix}USD`], egp = obj[`${prefix}EGP`];
  if (usd !== undefined || egp !== undefined) {
    obj[prefix] = { usd: usd || 0, egp: egp || 0 };
    delete obj[`${prefix}USD`];
    delete obj[`${prefix}EGP`];
  }
};

/** Transform debit note from Strapi (flat) to frontend (nested objects) */
export const transformDebitNoteFromStrapi = (item: any) => {
  const t = { ...item };
  ['amount', 'deduction', 'paid', 'netRemaining'].forEach(p => toNestedCurrency(t, p));
  return t;
};

/**
 * Clean data by removing fields that shouldn't be sent to Content Manager
 * and transforming field names to match Strapi schema
 */
export const cleanDataForSubmit = (data: any, collectionType: string, isUpdate = false) => {
  let cleaned = { ...data };

  // Remove fields that Content Manager handles automatically
  delete cleaned.id;
  delete cleaned.createdAt;
  delete cleaned.updatedAt;
  delete cleaned.publishedAt;

  // Remove frontend-only fields that don't exist in Strapi
  delete cleaned.level; // Rank level field (frontend-only for sorting/display)

  // Remove complex relations that should be handled separately
  // Keep debitNotes if it's an array of strings (documentIds for Strapi relation)
  // Remove it if it's an array of objects (complex nested data)
  if (cleaned.debitNotes) {
    if (Array.isArray(cleaned.debitNotes) && cleaned.debitNotes.length > 0) {
      // If first element is a string, it's an array of documentIds - keep it
      if (typeof cleaned.debitNotes[0] !== 'string') {
        // It's an array of objects - remove it
        delete cleaned.debitNotes;
      }
    } else if (typeof cleaned.debitNotes === 'object' && !Array.isArray(cleaned.debitNotes)) {
      // It's an object (like {count: 0}) - remove it
      delete cleaned.debitNotes;
    }
  }
  delete cleaned.invoiceItems; // Invoice items with count objects
  delete cleaned.deductions; // Deductions with count objects

  // Remove relation objects (keep only the ID fields like clientId, vesselId, etc.)
  delete cleaned.client;
  delete cleaned.customer;
  // For debit notes, requisitions and delivery notes, vessel is a string field,
  // not a relation - don't delete it
  const vesselIsPlainField = ([
    API_CONFIG.CONTENT_MANAGER_ENDPOINTS.DEBIT_NOTES,
    API_CONFIG.CONTENT_MANAGER_ENDPOINTS.REQUISITIONS,
    API_CONFIG.CONTENT_MANAGER_ENDPOINTS.DELIVERY_NOTES,
  ] as string[]).includes(collectionType);
  if (!vesselIsPlainField) {
    delete cleaned.vessel;
  }
  delete cleaned.foa;
  delete cleaned.provider;
  delete cleaned.branch;
  delete cleaned.safe;
  delete cleaned.employee;
  delete cleaned.transactions;
  delete cleaned.invoices;
  // Keep invoiceHireItems only when it's an array of documentIds (relation set)
  if (cleaned.invoiceHireItems !== undefined) {
    const ihi = cleaned.invoiceHireItems;
    if (!Array.isArray(ihi) || (ihi.length > 0 && typeof ihi[0] !== 'string')) {
      delete cleaned.invoiceHireItems;
    }
  }
  // For document types, category is an enum field (employee/car/crew), and for
  // providers/bills it's a plain string field - don't delete it for those types
  const categoryIsPlainField =
    collectionType === API_CONFIG.CONTENT_MANAGER_ENDPOINTS.DOCUMENT_TYPES ||
    collectionType === API_CONFIG.CONTENT_MANAGER_ENDPOINTS.PROVIDERS ||
    collectionType === API_CONFIG.CONTENT_MANAGER_ENDPOINTS.BILLS;
  if (!categoryIsPlainField) {
    delete cleaned.category;
  }
  delete cleaned.assignedManager;

  // Strapi's email type rejects empty strings ("must be a valid email") —
  // a blank optional email field means "unset", so send null instead.
  if (cleaned.email === '') {
    cleaned.email = null;
  }

  // Currency is a relation on most collections; if a populated object slipped
  // through, replace it with its documentId. An empty string means "not set".
  if (cleaned.currency && typeof cleaned.currency === 'object') {
    cleaned.currency = cleaned.currency.documentId || cleaned.currency.id || null;
  }
  if (cleaned.currency === '') {
    cleaned.currency = null;
  }

  // Convert frontend *Id fields to backend relation field names
  // Backend expects: "client": "documentId" not "clientId": "documentId"
  const relationFieldMapping: Record<string, string> = {
    clientId: 'client',
    customerId: 'customer',
    vesselId: 'vessel',
    foaId: 'foa',
    providerId: 'provider',
    branchId: 'branch',
    safeId: 'safe',
    categoryId: 'category',
    currencyId: 'currency',
    bankId: 'bank',
    employeeId: 'employee',
    invoiceId: 'invoice',
    pettyCashId: 'pettyCash',
    paymentOrderId: 'paymentOrder',
    invoiceHireId: 'invoiceHire',
    requisitionId: 'requisition',
    contractId: 'contract',
    billId: 'bill',
    carId: 'car',
    candidateId: 'candidate',
    assessmentPointId: 'assessmentPoint',
    letterId: 'letter',
  };

  Object.entries(relationFieldMapping).forEach(([frontendField, backendField]) => {
    if (cleaned[frontendField] !== undefined) {
      // Skip vesselId→vessel for debit notes (vessel is a string field there)
      if (backendField === 'vessel' && collectionType === API_CONFIG.CONTENT_MANAGER_ENDPOINTS.DEBIT_NOTES) {
        return;
      }
      // Skip categoryId→category for document types (category is an enum there)
      if (backendField === 'category' && collectionType === API_CONFIG.CONTENT_MANAGER_ENDPOINTS.DOCUMENT_TYPES) {
        return;
      }
      const value = cleaned[frontendField];
      if (value === '' || value === null) {
        cleaned[backendField] = null;
      } else {
        cleaned[backendField] = value;
      }
      delete cleaned[frontendField];
    }
  });

  // For updates, keep documentId separate (we'll use it in the URL)
  // For creates, remove it (it doesn't exist yet)
  if (!isUpdate) {
    delete cleaned.documentId;
  }

  // Collection-specific flattening/normalization (nested objects, empty dates)
  applyCollectionRules(cleaned, collectionType);

  // Transform field names (e.g., status -> bill_status)
  cleaned = transformFieldNames(cleaned, collectionType);

  console.log('🔄 [Admin Resources] Data transformation:', {
    original: data,
    cleaned: cleaned,
    collectionType: collectionType,
    isUpdate: isUpdate
  });

  return cleaned;
};
